import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { i18n } from '../i18n/i18n';
import {
  authCardClass,
  authErrorBannerClass,
  authInputClass,
  authLabelClass,
  authScreenShellClass,
} from './authUiClasses';
import { supabase } from '../services/supabaseClient';
import { isNative, oauthRedirectTo, openSignInPage } from '../platform/native';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, is_authenticated } = useAppContext();
  const navigate = useNavigate();

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (is_authenticated) {
      navigate('/');
    }
  }, [is_authenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      // Login successful - the context will handle the state update
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t('auth.login_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = async (provider: 'google' | 'facebook' | 'apple') => {
    // In the iOS/Android apps the sign-in page opens in the system browser and comes back to the app.
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: oauthRedirectTo(), skipBrowserRedirect: isNative },
    });
    if (error) setError(error.message);
    else if (isNative && data?.url) await openSignInPage(data.url);
  };

  return (
    <div className={authScreenShellClass}>
      <div className={authCardClass}>
        <h2 className="text-center text-gray-800 dark:text-gray-100 mb-8 text-3xl font-semibold">{i18n.t('login.title')}</h2>
        <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-950/40 rounded-lg border-l-4 border-indigo-500 dark:border-indigo-400 text-sm text-gray-700 dark:text-gray-200">
          {i18n.t('auth.beta_banner')}{' '}
          <Link to="/signup" className="text-indigo-600 dark:text-indigo-400 underline font-medium">
            {i18n.t('auth.beta_banner_link')}
          </Link>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label htmlFor="email" className={authLabelClass}>
              {i18n.t('login.email_label')}:
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className={authInputClass}
            />
          </div>
          <div className="mb-5">
            <label htmlFor="password" className={authLabelClass}>
              {i18n.t('login.password_label')}:
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className={authInputClass}
            />
          </div>
          {error && (
            <div className={authErrorBannerClass}>
              {error}
            </div>
          )}
          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full h-12 py-0 flex items-center justify-center bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-none rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:not-disabled:-translate-y-0.5 hover:not-disabled:shadow-lg hover:not-disabled:shadow-indigo-400/30 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? i18n.t('login.logging_in') : i18n.t('login.login_button')}
          </button>
        </form>
        <div className="my-4 flex items-center">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
          <span className="px-3 text-gray-500 dark:text-gray-400 text-sm">{i18n.t('auth.or')}</span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600" />
        </div>
        <div className="mt-4 flex flex-col items-center">
          <button
            type="button"
            onClick={() => handleOAuthLogin('google')}
            className="w-full max-w-[320px] mx-auto inline-flex items-center justify-center gap-2 h-12 px-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100"
          >
            <img src="/google_g.webp" alt="" aria-hidden="true" className="w-5 h-5" />
            <span>{i18n.t('auth.login_google')}</span>
          </button>
        </div>
        <div className="mt-3 space-y-2 flex flex-col items-center">
          <button
            type="button"
            onClick={() => handleOAuthLogin('facebook')}
            className="w-full max-w-[320px] mx-auto inline-flex items-center justify-center gap-2 h-12 px-4 rounded-lg bg-[#1877F2] text-white"
          >
            <img src="/Facebook_Logo.png" alt="" aria-hidden="true" className="w-5 h-5" />
            <span>{i18n.t('auth.login_facebook')}</span>
          </button>
          <button
            type="button"
            onClick={() => handleOAuthLogin('apple')}
            className="w-full max-w-[320px] mx-auto inline-flex items-center justify-center gap-2 h-12 px-4 rounded-lg bg-black text-white dark:border dark:border-gray-500"
          >
            <img src="/Apple_Logo.png" alt="" aria-hidden="true" className="w-5 h-5 invert" />
            <span>{i18n.t('auth.login_apple')}</span>
          </button>
        </div>
        <div className="mt-4 text-center text-sm">
          <Link to="/forgot-password" className="text-indigo-600 dark:text-indigo-400 underline">
            {i18n.t('auth.forgot_link')}
          </Link>
        </div>
        <div className="mt-4 text-center text-sm text-gray-600 dark:text-gray-300">
          {i18n.t('auth.no_account')}{' '}
          <Link to="/signup" className="text-indigo-600 dark:text-indigo-400 underline">
            {i18n.t('auth.create_one')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
